import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEnvelope,
  faLock,
  faEye,
  faEyeSlash,
  faUser,
  faMicrophone,
  faVideo,
  faNewspaper,
  faCalendarAlt,
  faShieldAlt,
  faCamera,
  faChartLine,
  faUsers,
  faBullhorn
} from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import { toast } from 'react-toastify';

const PressLogin = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Check if user is already logged in
    const token = localStorage.getItem('pressToken');
    if (token) {
      navigate('/press/dashboard');
    }
  }, [navigate]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    // Frontend validation before API call
    const { email, password } = formData;
    if (!email || !password) {
      setError('Email and password are required');
      setIsLoading(false);
      return;
    }

    const identifier = String(email).trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(identifier)) {
      setError('Invalid email format');
      setIsLoading(false);
      return;
    }

    try {
      const loginResponse = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/press/login`, {
        email: formData.email,
        password: formData.password
      });

      // Handle backend responses
      if (loginResponse.data?.success === false) {
        setError(loginResponse.data.message || 'Login failed');
        setIsLoading(false);
        return;
      }

      if (loginResponse.data?.token) {
        // Store press conference specific data
        localStorage.setItem('pressToken', loginResponse.data.token);
        localStorage.setItem('pressUser', JSON.stringify(loginResponse.data.user));
        localStorage.setItem('userRole', 'press');

        toast.success('Login successful! Redirecting to Press Dashboard...');
        setTimeout(() => {
          navigate('/press/dashboard');
        }, 1500);
      }
    } catch (err) {
      if (err.response?.status === 400) {
        const msg = err.response?.data?.message;
        if (msg === 'Email and password are required') {
          setError('Email and password are required');
        } else if (msg === 'Invalid email format') {
          setError('Invalid email format');
        } else if (msg === 'Invalid credentials') {
          setError('Invalid credentials');
        } else if (msg === 'Account not verified. Please verify your account before logging in.') {
          setError('Account not verified. Please verify your account before logging in.');
        } else {
          setError(msg || 'Login failed');
        }
      } else {
        setError(err.response?.data?.message || 'Login failed');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center py-8 px-4 sm:px-6 lg:px-8 pt-20">
      <div className="w-full max-w-5xl mx-auto bg-white rounded-[2rem] shadow-2xl shadow-teal-200/50 overflow-hidden border border-slate-100">
        <div className="flex flex-col lg:flex-row min-h-[600px]">
          {/* Left side - Company Branding */}
          <div className="hidden lg:block lg:w-5/12 bg-gradient-to-br from-teal-700 via-cyan-600 to-teal-900 relative overflow-hidden text-white">
            {/* Background Pattern Overlay */}
            <div className="absolute inset-0 opacity-10" style={{ backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`, backgroundSize: '24px 24px' }}></div>

            {/* Animated Background Elements */}
            <motion.div
              animate={{
                scale: [1, 1.2, 1],
                rotate: [0, 90, 0],
                opacity: [0.05, 0.1, 0.05]
              }}
              transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
              className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl -mr-32 -mt-32"
            ></motion.div>

            <motion.div
              animate={{
                scale: [1, 1.5, 1],
                x: [0, 50, 0],
                opacity: [0.1, 0.2, 0.1]
              }}
              transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
              className="absolute bottom-0 left-0 w-64 h-64 bg-teal-400 rounded-full blur-3xl -ml-20 -mb-20"
            ></motion.div>

            <div className="relative flex flex-col justify-between h-full p-12 z-10">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/10 backdrop-blur-md rounded-full border border-white/20 mb-8">
                  <div className="w-2 h-2 bg-teal-300 rounded-full animate-pulse shadow-[0_0_8px_rgba(94,234,212,0.8)]"></div>
                  <span className="text-[10px] font-black uppercase tracking-[0.2em] text-teal-100">Live Press Portal</span>
                </div>

                <div className="w-20 h-20 mb-8 bg-gradient-to-tr from-white/20 to-white/5 backdrop-blur-xl border border-white/30 rounded-[2rem] flex items-center justify-center shadow-2xl relative group overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-tr from-teal-400/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                  <FontAwesomeIcon icon={faCamera} className="text-3xl text-white drop-shadow-md group-hover:scale-110 transition-transform duration-500" />
                </div>

                <h2 className="text-4xl font-[1000] tracking-tight mb-4 leading-[1.1] drop-shadow-sm">
                  Connect with the <span className="text-teal-300">Media</span>
                </h2>
                <p className="text-teal-50/80 font-medium tracking-wide text-base max-w-xs leading-relaxed">
                  Your official gateway to organized press events, live broadcasting, and media management.
                </p>
              </motion.div>

              <div className="space-y-6">
                <div className="space-y-4">
                  {[
                    { icon: faMicrophone, title: "Host Events", desc: "Manage scheduled press meets", color: "emerald", delay: 0.1 },
                    { icon: faVideo, title: "Live Streaming", desc: "Broadcast HD content live", color: "blue", delay: 0.2 },
                    { icon: faNewspaper, title: "Media Outreach", desc: "Broad press distribution", color: "purple", delay: 0.3 }
                  ].map((item, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: item.delay, duration: 0.5 }}
                      className="group flex items-center gap-4 bg-white/5 hover:bg-white/10 backdrop-blur-md border border-white/10 hover:border-white/20 rounded-2xl p-4 transition-all duration-300 cursor-default"
                    >
                      <div className={`w-12 h-12 bg-${item.color}-500/20 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-500`}>
                        <FontAwesomeIcon icon={item.icon} className={`text-${item.color}-300 text-xl`} />
                      </div>
                      <div>
                        <span className="font-black tracking-wide text-sm block group-hover:text-teal-300 transition-colors uppercase">{item.title}</span>
                        <p className="text-[11px] text-teal-100/60 uppercase tracking-widest font-bold font-sans">{item.desc}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>

                <div className="pt-8 border-t border-white/10 flex items-center justify-between">
                  <div className="flex items-center gap-3 text-teal-100/60">
                    <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center">
                      <FontAwesomeIcon icon={faShieldAlt} className="text-xs" />
                    </div>
                    <span className="text-[10px] font-black uppercase tracking-[0.2em]">Secure Platform</span>
                  </div>
                  <div className="flex -space-x-2">
                    {[1, 2, 3].map(i => (
                      <div key={i} className="w-6 h-6 rounded-full border-2 border-teal-800 bg-teal-600 flex items-center justify-center text-[8px] font-bold">
                        {i}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right side - Login Form */}
          <div className="w-full lg:w-7/12 p-8 sm:p-12 flex flex-col justify-center bg-white relative">
            {/* Mobile Hearing - Panel Name */}
            <div className="lg:hidden text-center mb-6 border-b border-slate-100 pb-4">
              <div className="w-12 h-12 mx-auto mb-3 bg-teal-50 rounded-2xl flex items-center justify-center shadow-inner">
                <FontAwesomeIcon icon={faCamera} className="text-xl text-teal-600" />
              </div>
              <h2 className="text-2xl font-[1000] text-teal-900 uppercase tracking-tight">
                Press Conference
              </h2>
            </div>

            <div className="mb-8 group">
              <h1 className="text-3xl sm:text-4xl font-[1000] text-slate-900 tracking-tighter mb-4 uppercase leading-[1.1]">
                Sign In <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-600 to-cyan-500">Press Portal</span>
              </h1>
              <div className="w-12 h-1.5 bg-teal-600 rounded-full mb-4 transition-all duration-500 group-hover:w-20"></div>
              <p className="text-sm font-bold text-slate-500 tracking-wide flex items-center gap-2">
                <span className="w-1 h-4 bg-teal-600 rounded-full"></span>
                Secure access to your Press Conference account
              </p>
            </div>

            <form className="space-y-4" onSubmit={handleSubmit}>
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-red-50 border border-red-200 text-red-700 p-2 rounded-md text-xs font-medium"
                >
                  {error}
                </motion.div>
              )}

              <div className="space-y-4">
                <div className="space-y-1.5">
                  <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest pl-1">
                    Email Address
                  </label>
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                      <FontAwesomeIcon icon={faEnvelope} className="text-slate-400 group-focus-within:text-teal-500 transition-colors" />
                    </div>
                    <input
                      name="email"
                      type="email"
                      required
                      className="block w-full pl-10 pr-4 py-3 bg-slate-50 border-2 border-slate-100 rounded-xl focus:bg-white focus:ring-4 focus:ring-teal-50 focus:border-teal-500 outline-none transition-all text-sm font-bold text-slate-900"
                      placeholder="Enter your registered email address"
                      value={formData.email}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest pl-1">
                    Password
                  </label>
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                      <FontAwesomeIcon icon={faLock} className="text-slate-400 group-focus-within:text-teal-500 transition-colors" />
                    </div>
                    <input
                      name="password"
                      type={showPassword ? "text" : "password"}
                      required
                      className="block w-full pl-10 pr-12 py-3 bg-slate-50 border-2 border-slate-100 rounded-xl focus:bg-white focus:ring-4 focus:ring-teal-50 focus:border-teal-500 outline-none transition-all text-sm font-bold text-slate-900"
                      placeholder="Enter your password"
                      value={formData.password}
                      onChange={handleChange}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-4 flex items-center"
                    >
                      <FontAwesomeIcon
                        icon={showPassword ? faEyeSlash : faEye}
                        className="text-slate-400 hover:text-slate-600 transition-colors"
                      />
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between mt-4">
                <div className="flex items-center gap-2">
                  <input
                    id="remember-me"
                    name="remember-me"
                    type="checkbox"
                    className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-slate-300 rounded"
                  />
                  <label htmlFor="remember-me" className="block text-xs font-bold text-slate-600">
                    Keep me logged in
                  </label>
                </div>
                <Link to="/press/forget-password" className="text-[10px] sm:text-xs font-black text-teal-600 hover:text-teal-700 tracking-wide uppercase transition-colors">
                  Forgot Password?
                </Link>
              </div>

              <div className="mt-6 pt-2">
                <button
                  type="submit"
                  disabled={isLoading}
                  className={`w-full flex justify-center items-center py-3.5 px-4 rounded-xl shadow-lg shadow-teal-200/50 text-sm font-black text-white bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 active:scale-[0.98] transition-all uppercase tracking-widest ${isLoading ? "opacity-70 cursor-not-allowed" : ""}`}
                >
                  {isLoading ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Authenticating...
                    </>
                  ) : (
                    'Secure Login'
                  )}
                </button>
              </div>

              <div className="text-center mt-5">
                <span className="text-xs font-bold text-slate-500">Don't have an account? </span>
                <Link to="/press/register" className="text-teal-600 hover:text-teal-700 font-black text-xs uppercase tracking-widest pl-1">
                  Sign up
                </Link>
              </div>
            </form>

            <div className="mt-8 pt-6 border-t-2 border-slate-50">
              {/* Quick Login Buttons */}
              <div className="space-y-3">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">Alternative Access</p>
                <div className="grid grid-cols-3 gap-2 sm:gap-3">
                  <Link
                    to="/login"
                    className="group flex flex-col items-center justify-center p-3 bg-slate-50 border-2 border-slate-100 hover:bg-indigo-50 hover:border-indigo-200 rounded-xl transition-all"
                  >
                    <FontAwesomeIcon icon={faUser} className="text-slate-400 group-hover:text-indigo-500 mb-1 transition-colors" />
                    <span className="text-[9px] font-black text-slate-500 group-hover:text-indigo-600 uppercase tracking-widest">General</span>
                  </Link>

                  <Link
                    to="/raise/login"
                    className="group flex flex-col items-center justify-center p-3 bg-slate-50 border-2 border-slate-100 hover:bg-purple-50 hover:border-purple-200 rounded-xl transition-all"
                  >
                    <FontAwesomeIcon icon={faBullhorn} className="text-slate-400 group-hover:text-purple-500 mb-1 transition-colors" />
                    <span className="text-[9px] font-black text-slate-500 group-hover:text-purple-600 uppercase tracking-widest">Raise</span>
                  </Link>

                  <Link
                    to="/podcast/login"
                    className="group flex flex-col items-center justify-center p-3 bg-slate-50 border-2 border-slate-100 hover:bg-emerald-50 hover:border-emerald-200 rounded-xl transition-all"
                  >
                    <FontAwesomeIcon icon={faMicrophone} className="text-slate-400 group-hover:text-emerald-500 mb-1 transition-colors" />
                    <span className="text-[9px] font-black text-slate-500 group-hover:text-emerald-600 uppercase tracking-widest">Podcast</span>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PressLogin;
